import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { credentialingService } from '../../../services/credentialingService';

const ComplianceDocumentsPanel = ({ refreshTrigger }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDoc, setSelectedDoc] = useState(null);

  useEffect(() => {
    loadDocuments();
  }, [refreshTrigger]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const data = await credentialingService?.getComplianceDocuments();
      setDocuments(data);
    } catch (err) {
      setError(err?.message || 'Failed to load compliance documents');
    } finally {
      setLoading(false);
    }
  };

  const getDocumentIcon = (type) => {
    switch (type) {
      case 'license':
        return 'Award';
      case 'certification':
        return 'FileCheck';
      case 'insurance_policy':
        return 'Shield';
      case 'credentialing_application':
        return 'FileText';
      case 'cme_certificate':
        return 'GraduationCap';
      case 'background_check':
        return 'UserCheck';
      default:
        return 'File';
    }
  };

  const getDocumentColor = (type) => {
    switch (type) {
      case 'license':
        return 'text-primary';
      case 'certification':
        return 'text-success';
      case 'insurance_policy':
        return 'text-warning';
      case 'credentialing_application':
        return 'text-secondary';
      default:
        return 'text-muted-foreground';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024)?.toFixed(1) + ' KB';
    return (bytes / 1048576)?.toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 border border-destructive rounded-lg p-4">
        <p className="text-destructive text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {documents?.length} document{documents?.length !== 1 ? 's' : ''}
        </p>
        <Button variant="outline" size="sm" iconName="Upload" iconPosition="left">
          Upload Document
        </Button>
      </div>

      <div className="space-y-3">
        {documents?.map((doc) => (
          <div
            key={doc?.id}
            className="bg-muted/30 border border-border rounded-lg p-4 transition-smooth hover:shadow-elevation-2 cursor-pointer"
            onClick={() => setSelectedDoc(doc)}
          >
            <div className="flex items-start gap-4">
              <div className={`flex-shrink-0 ${getDocumentColor(doc?.documentType)}`}>
                <Icon name={getDocumentIcon(doc?.documentType)} size={24} />
              </div>
              
              <div className="flex-1 min-w-0">
                <h4 className="text-sm md:text-base font-semibold text-foreground mb-1 truncate">
                  {doc?.documentName}
                </h4>
                
                <div className="flex flex-wrap items-center gap-2 md:gap-3 text-xs text-muted-foreground mb-2">
                  <span>{doc?.documentType?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}</span>
                  <span>•</span>
                  <span>{formatFileSize(doc?.fileSize)}</span>
                  {doc?.version && (
                    <>
                      <span>•</span>
                      <span>v{doc?.version}</span>
                    </>
                  )}
                  <span>•</span>
                  <span>Uploaded {formatDate(doc?.uploadDate)}</span>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  {doc?.expirationDate && (
                    <div className="flex items-center gap-1 text-xs">
                      <Icon name="Calendar" size={12} className="text-muted-foreground" />
                      <span className="text-muted-foreground">Expires: {formatDate(doc?.expirationDate)}</span>
                    </div>
                  )}
                  {doc?.verificationStatus && (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      doc?.verificationStatus === 'verified' ? 'bg-success/10 text-success' :
                      doc?.verificationStatus === 'pending'? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground'
                    }`}>
                      {doc?.verificationStatus?.replace(/\b\w/g, l => l?.toUpperCase())}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Download"
                  onClick={(e) => {
                    e?.stopPropagation();
                  }}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Eye"
                  onClick={(e) => {
                    e?.stopPropagation();
                    setSelectedDoc(doc);
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {documents?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
          <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
            <Icon name="FolderOpen" size={32} color="var(--color-muted-foreground)" />
          </div>
          <p className="text-base font-medium text-foreground mb-2">No Documents</p>
          <p className="text-sm text-muted-foreground mb-4">Upload compliance documents to get started</p>
          <Button variant="outline" iconName="Upload" iconPosition="left">
            Upload Document
          </Button>
        </div>
      )}

      {selectedDoc && (
        <div
          className="fixed inset-0 z-[1000] bg-background flex items-center justify-center p-4"
          onClick={() => setSelectedDoc(null)}
        >
          <div
            className="bg-card rounded-xl shadow-elevation-5 border border-border max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e?.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className={getDocumentColor(selectedDoc?.documentType)}>
                  <Icon name={getDocumentIcon(selectedDoc?.documentType)} size={24} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base md:text-lg font-semibold text-foreground truncate">
                    {selectedDoc?.documentName}
                  </h3>
                  <p className="text-xs md:text-sm text-muted-foreground">Uploaded {formatDate(selectedDoc?.uploadDate)}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                iconName="X"
                onClick={() => setSelectedDoc(null)}
              />
            </div>
            
            <div className="p-4 md:p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="bg-muted rounded-lg p-8 text-center">
                <Icon name="FileText" size={64} color="var(--color-muted-foreground)" />
                <p className="text-sm text-muted-foreground mt-4">Document preview</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceDocumentsPanel;